import { Location, PlatformLocation } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { ActivatedRoute, CanActivate, Router, UrlTree } from '@angular/router';
import {
  MsalGuardConfiguration,
  MsalService, MSAL_GUARD_CONFIG,
  } from '@azure/msal-angular';
import { AuthenticationResult, BrowserUtils, InteractionType, PopupRequest, RedirectRequest, UrlString } from '@azure/msal-browser';
import {
  UrlUtils,
  WindowUtils,
  InteractionRequiredAuthError,
  Configuration,
} from 'msal';
import { Observable, of, TimeoutError } from 'rxjs';
import { concatMap, catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { __awaiter } from 'tslib';
import { GLOBAL_CONFIG } from '../data/globalConfig';
import { AuthorizationService } from '../services/auth/authorization.service';

@Injectable({ providedIn: 'root' })
export class CustomMsalGuard implements CanActivate {
  private msalGuardConfig: MsalGuardConfiguration;
  constructor(
    @Inject(MSAL_GUARD_CONFIG) msalGuardConfig: MsalGuardConfiguration,
    private authService: MsalService,
    private location: Location,
    private customAuthService: AuthorizationService
  ) {
    this.msalGuardConfig = msalGuardConfig;
  }
  /**
   * Builds the absolute url for the destination page
   * @param path Relative path of requested page
   * @returns Full destination url
   */
  getDestinationUrl(path) {
    // Absolute base url for the application (default to origin if base element not present)
    const baseElements = document.getElementsByTagName('base');
    const baseUrl = this.location.normalize(
      baseElements.length ? baseElements[0].href : window.location.origin
    );
    // Path of page (including hash, if using hash routing)
    const pathUrl = this.location.prepareExternalUrl(path);
    // Hash location strategy
    if (pathUrl.startsWith('#')) {
      return `${baseUrl}/${pathUrl}`;
    }
    // If using path location strategy, pathUrl will include the relative portion of the base path (e.g. /base/page).
    // Since baseUrl also includes /base, can just concatentate baseUrl + path
    return `${baseUrl}${path}`;
  }
  /**
   * Interactively prompt the user to login
   * @param url Path of the requested page
   */
  loginInteractively(state): Observable<boolean>  {
    const authRequest = typeof this.msalGuardConfig.authRequest === "function"
    ? this.msalGuardConfig.authRequest(this.authService, state)
    : { ...this.msalGuardConfig.authRequest };

    //return __awaiter(this, void 0, void 0, function*() {
    if (this.msalGuardConfig.interactionType === InteractionType.Popup) {
      return this.authService
        .loginPopup(authRequest as PopupRequest)
        .pipe(map((response: AuthenticationResult)=> {
          this.authService.getLogger().verbose("Guard - login by popup successful, can activate, setting active account");
          this.authService.instance.setActiveAccount(response.account);
          return true;
          })
        );

        //then(() =>  this.authService.checkUserRoles(route.data.expectedRoles))
        //.catch(() => false);
    }
    // const redirectStartPage = this.getDestinationUrl(state.url);
    const redirectStartPage = GLOBAL_CONFIG.config.redirectUri;
    return this.authService.loginRedirect({
      redirectStartPage,
      ...authRequest
      } as RedirectRequest)
      .pipe(
          map(() => false)
    );
  
  }
  canActivate(route, state): Observable<boolean|UrlTree>  {
    this.authService
      .getLogger()
      .verbose('location change event from old url to new url');
    // If a page with MSAL Guard is set as the redirect for acquireTokenSilent,
    // short-circuit to prevent redirecting or popups.
    if (UrlString.hashContainsKnownProperties(window.location.hash) && BrowserUtils.isInIframe()) {
      this.authService
        .getLogger()
        .warning(
          'redirectUri set to page with MSAL Guard. It is recommended to not set redirectUri to a page that requires authentication.'
        );
      return of(false);
    }
    return this.authService.handleRedirectObservable()
    .pipe(
      concatMap(() => {
          if (!this.authService.instance.getAllAccounts().length) {
              if (state) {
                  this.authService.getLogger().verbose("Guard - no accounts retrieved, log in required to activate");
                  return this.loginInteractively(state);
              } 
              this.authService.getLogger().verbose("Guard - no accounts retrieved, no state, cannot load");
              return of(false);
          }

          this.authService.getLogger().verbose("Guard - at least 1 account exists, can activate or load");
          return of(true);

      })
  );
  }
}
    /*
    if (!this.authService.instance.getAllAccounts().length) {
      return this.loginInteractively(state);
    }
    return this.authService
      .acquireTokenPopup({
        scopes: [this.msalConfig.auth.clientId],
        forceRefresh: false,
      })
      .then(() => this.customAuthService.checkUserRoles(route.data.expectedRoles))
      .catch((error) => {
        if (
          InteractionRequiredAuthError.isInteractionRequiredError(
            error.errorCode
          )
        ) {
          this.authService
            .getLogger()
            .info(
              `Interaction required error in MSAL Guard, prompting for interaction.`
            );
          return this.loginInteractively(route, state.url);
        }
        this.authService
          .getLogger()
          .error(`Non-interaction error in MSAL Guard: ${error.errorMessage}`);
        throw error;
      });
      */
